import React, { useState, useEffect } from 'react';
import { ClientLink, PatientLink } from '../components/EntityLinks';
import { useNavigate } from 'react-router-dom';
import {
  Search, Phone, MapPin, Calendar, FileText,
  Eye, Calculator, Settings, Cog, Shield, Loader2,
  ChevronRight, ChevronDown, Wallet, UserPlus, CalendarCheck, UserCog, User, X,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import FilterableTh from '../components/FilterableTh';
import apiClient from '../../../api/api';
import { formatMobileNumber } from '../../../utils/phoneFormat';
import { withHonorific } from '../../../utils/clientName';
import PresetManager from '../service_quotes/PresetManager';
import useAutoRefresh from '../../../hooks/useAutoRefresh';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import NotificationRecipientsModal from './NotificationRecipientsModal';

const STATUS_TABS = ['All', 'New Lead', 'Pending', 'Contacted', 'Confirmed', 'Cancelled', 'Booking Created'];

const TAB_TO_STATUS = {
  'New Lead':       'NEW_LEAD',
  'Pending':        'PENDING',
  'Contacted':      'CONTACTED',
  'Confirmed':      'CONFIRMED',
  'Cancelled':      'CANCELLED',
  'Booking Created':'BOOKING_CREATED',
};

const STATUS_CONFIG = {
  NEW_LEAD:        { dot: 'bg-purple-400', text: 'text-purple-700', label: 'New Lead' },
  PENDING:         { dot: 'bg-amber-400',  text: 'text-amber-700',  label: 'Pending' },
  CONTACTED:       { dot: 'bg-blue-400',   text: 'text-blue-700',   label: 'Contacted' },
  CONFIRMED:       { dot: 'bg-emerald-500',text: 'text-emerald-700',label: 'Confirmed' },
  CANCELLED:       { dot: 'bg-red-400',    text: 'text-red-700',    label: 'Cancelled' },
  BOOKING_CREATED: { dot: 'bg-indigo-500', text: 'text-indigo-700', label: 'Booking Created' },
};

// PENDING here means the booking exists but no staff has been assigned yet
// (bookings.status flips to ACTIVE once staffAssignmentController assigns
// staff — see staffAssignmentController.js:410).
const BOOKING_STATUS_CONFIG = {
  PENDING: { dot: 'bg-amber-400',   text: 'text-amber-700',   label: 'Yet to Assign Staff' },
  ACTIVE:  { dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'Active' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { dot: 'bg-slate-400', text: 'text-slate-600', label: status };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const BookingStatusBadge = ({ status }) => {
  const cfg = BOOKING_STATUS_CONFIG[status] || { dot: 'bg-slate-400', text: 'text-slate-500', label: status };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const PRIORITY_TYPE = 'PRIORITY_MEMBERSHIP';
const UNASSIGNED = '__UNASSIGNED__';

const EnteredViaBadge = ({ value }) => {
  const isProxy = value === 'PROXY';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
      isProxy ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
    }`}>
      {isProxy ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
      {isProxy ? 'Proxy' : 'Client'}
    </span>
  );
};

const formatDate = (v) =>
  v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const formatDateTime = (v) =>
  v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

// Returns the next actionable stage for a request, or null once there's nothing
// left to do from this table (booking created + staff assigned).
const getStageAction = (r) => {
  const status = r.status || 'PENDING';

  if (status === 'BOOKING_CREATED') {
    if (r.booking_status === 'PENDING' && r.booking_id) {
      return { icon: UserPlus, title: 'Assign Staff', path: `/admin/bookings/${r.booking_id}/staff-assignment` };
    }
    return null; // staff already assigned — booking module owns it from here
  }

  // `status` is a free-text column, not a DB enum — besides NEW_LEAD/PENDING it can
  // also be CONTACTED/CONFIRMED (set manually via the generic status-update endpoint)
  // or any other legacy value. Anything short of BOOKING_CREATED/CANCELLED is still
  // pre-booking and should walk through the same quote → payment → booking stages.
  if (status !== 'CANCELLED') {
    if (!r.active_quote_id) {
      return { icon: Calculator, title: 'Create Quote', path: `/admin/modular-quote-builder/${r.request_id}` };
    }
    const isFullyPaid = r.quote_total_amount != null
      && Number(r.quote_total_paid) >= Number(r.quote_total_amount);
    if (!isFullyPaid) {
      return { icon: Wallet, title: 'Record Payment', path: `/admin/quotations/${r.active_quote_id}`, pickQuote: true };
    }
    // A Priority Membership is just a registration fee — nothing to book once it's paid.
    if (r.service_type === PRIORITY_TYPE) return null;
    // Fully paid — ready to convert. There's no standalone "create booking" page;
    // the Proceed/convert action lives on the request summary page itself.
    return { icon: CalendarCheck, title: 'Create Booking', path: `/admin/service-requests/${r.request_id}/summary` };
  }

  return null;
};

const formatMoney = (v) =>
  `Rs. ${Number(v || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const QuoteOption = ({ quote, isActive, onSelect }) => {
  const [open, setOpen] = useState(false);
  const items = quote.line_items || [];
  const total = Number(quote.total_amount || 0);
  const paid = Number(quote.total_paid || 0);
  const fullyPaid = total > 0 && paid >= total;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="p-1 rounded text-slate-400 hover:bg-slate-100"
          title={open ? 'Hide line items' : 'Show line items'}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900">{quote.estimate_number || 'Quotation'}</span>
            {isActive && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">Active</span>
            )}
            {fullyPaid && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Fully paid</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {formatDate(quote.created_at)} · {formatMoney(total)} · Paid {formatMoney(paid)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onSelect(quote)}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-500"
        >
          Record Payment
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50 px-3 py-2">
          {items.length === 0 ? (
            <p className="text-xs text-slate-400 py-1">No line items.</p>
          ) : (
            <table className="w-full text-xs">
              <tbody className="divide-y divide-slate-100">
                {items.map(li => (
                  <tr key={li.line_item_id}>
                    <td className="py-1.5 pr-2 text-slate-700">{li.description || li.item_type}</td>
                    <td className="py-1.5 px-2 text-slate-500 whitespace-nowrap">
                      {Number(li.quantity)} × {formatMoney(li.unit_price)}
                    </td>
                    <td className="py-1.5 pl-2 text-right font-medium text-slate-800 whitespace-nowrap">
                      {formatMoney(li.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const ServiceRequests = () => {
  const navigate = useNavigate();
  // { request, quotes, loading } while the "which quotation?" picker is open
  const [paymentPicker, setPaymentPicker] = useState(null);
  const [requests, setRequests]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [search, setSearch]                   = useState('');
  const [activeTab, setActiveTab]             = useState('All');
  const [priorityOnly, setPriorityOnly]       = useState(false);
  const [serviceFilter, setServiceFilter]     = useState('ALL');
  const [coordinatorFilter, setCoordinatorFilter] = useState('ALL');
  const [enteredViaFilter, setEnteredViaFilter]   = useState('ALL');
  const [showPresetManager, setShowPresetManager] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const { hasPermission } = useAdminAuth();
  const canManageNotifications = hasPermission ? hasPermission('SERVICE_REQUEST_NOTIFICATIONS') : false;

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      if (!silent) setError(null);
      const res = await apiClient.getAllServiceRequests();
      const next = res.data || [];
      // The 5s background poll usually returns identical data; keeping the old
      // array reference avoids re-rendering the whole table (which causes paint
      // glitches / ghosted rows on low-end GPUs).
      setRequests(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    } catch (err) {
      if (!silent) setError('Failed to load service requests.');
      else console.error('ServiceRequests silent refresh error:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // New leads arrive from the public site continuously, so keep this list
  // fresh in the background; pause while the preset manager modal is open.
  useAutoRefresh(() => fetchRequests({ silent: true }), {
    intervalMs: 5000,
    enabled: !showPresetManager,
  });

  // With a single quotation we go straight to it; with several, ask which one.
  const handleStageAction = async (r, action) => {
    if (!action.pickQuote) {
      navigate(action.path);
      return;
    }
    setPaymentPicker({ request: r, quotes: [], loading: true });
    try {
      const res = await apiClient.getServiceRequestQuoteList(r.request_id);
      const quotes = res.data || [];
      if (quotes.length <= 1) {
        setPaymentPicker(null);
        navigate(action.path);
      } else {
        setPaymentPicker({ request: r, quotes, loading: false });
      }
    } catch (err) {
      console.error('Failed to load quotations:', err);
      setPaymentPicker({
        request: r, quotes: [], loading: false,
        error: err.message || 'Failed to load quotations.',
        fallbackPath: action.path,
      });
    }
  };

  const filtered = requests.filter(r => {
    const statusMatch = activeTab === 'All' || r.status === TAB_TO_STATUS[activeTab];
    const q = search.trim().toLowerCase();
    const textMatch = !q || [r.payer_name, r.patient_name, r.payer_mobile, r.service_type, r.service_request_code]
      .some(v => v?.toLowerCase().includes(q));
    const typeMatch = !priorityOnly || r.service_type === PRIORITY_TYPE;
    const serviceMatch = serviceFilter === 'ALL' || r.service_type === serviceFilter;
    const coordinatorMatch = coordinatorFilter === 'ALL'
      || (coordinatorFilter === UNASSIGNED ? !r.coordinator_name : r.coordinator_name === coordinatorFilter);
    const viaMatch = enteredViaFilter === 'ALL' || (r.entered_via === 'PROXY' ? 'PROXY' : 'CLIENT') === enteredViaFilter;
    return statusMatch && textMatch && typeMatch && serviceMatch && coordinatorMatch && viaMatch;
  });

  const serviceOptions = [
    { key: 'ALL', label: 'All Services', count: requests.length },
    ...[...new Set(requests.map(r => r.service_type).filter(Boolean))].sort().map(s => ({
      key: s,
      label: s === PRIORITY_TYPE ? 'Priority Membership' : s,
      count: requests.filter(r => r.service_type === s).length,
    })),
  ];

  const coordinatorOptions = [
    { key: 'ALL', label: 'All Coordinators', count: requests.length },
    ...[...new Set(requests.map(r => r.coordinator_name).filter(Boolean))].sort().map(n => ({
      key: n,
      label: n,
      count: requests.filter(r => r.coordinator_name === n).length,
    })),
    { key: UNASSIGNED, label: 'Unassigned', count: requests.filter(r => !r.coordinator_name).length },
  ];

  const enteredViaOptions = [
    { key: 'ALL', label: 'All', count: requests.length },
    { key: 'CLIENT', label: 'Client', count: requests.filter(r => r.entered_via !== 'PROXY').length },
    { key: 'PROXY', label: 'Proxy', count: requests.filter(r => r.entered_via === 'PROXY').length },
  ];

  const priorityCount = requests.filter(r => r.service_type === PRIORITY_TYPE).length;

  const counts = {
    All: requests.length,
    ...Object.fromEntries(
      STATUS_TABS.filter(t => t !== 'All').map(tab => [
        tab,
        requests.filter(r => r.status === TAB_TO_STATUS[tab]).length,
      ])
    ),
  };

  if (loading) {
    return (
      <AdminLayout title="Service Requests" subtitle="Loading…">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Service Requests">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Service Requests"
      subtitle={`${requests.length} total request${requests.length !== 1 ? 's' : ''}`}
      actions={
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 bg-white rounded-lg">
            <Shield className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-sm font-medium text-slate-500">Proxy Mode</span>
            <button
              type="button"
              onClick={() => navigate('/admin/proxy-service-requests')}
              role="switch"
              aria-checked={false}
              className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent bg-slate-200 transition-colors duration-200 ease-in-out focus:outline-none"
            >
              <span className="pointer-events-none inline-block h-4 w-4 translate-x-1 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ease-in-out" />
            </button>
          </div>
          <button
            onClick={() => setShowPresetManager(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-500 transition-colors"
          >
            <Settings className="w-3.5 h-3.5" />
            Manage Presets
          </button>
          {canManageNotifications && (
            <button
              onClick={() => setShowNotificationSettings(true)}
              title="New request email alerts"
              aria-label="New request email alerts"
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors"
            >
              <Cog className="w-4 h-4" />
            </button>
          )}
        </div>
      }
    >
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1 w-fit flex-wrap">
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                activeTab === tab
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab}
              <span className="ml-1.5 tabular-nums text-slate-400">{counts[tab]}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setPriorityOnly(v => !v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors w-fit ${
            priorityOnly
              ? 'bg-violet-600 text-white border-violet-600'
              : 'bg-white text-violet-700 border-violet-200 hover:bg-violet-50'
          }`}
        >
          Priority Membership
          <span className="ml-1.5 tabular-nums opacity-70">{priorityCount}</span>
        </button>

        <div className="relative sm:ml-auto">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, phone, service…"
            className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none w-64"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Request</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Care Profile</th>
                <FilterableTh label="Service" className="text-left px-4 py-3 text-xs"
                  value={serviceFilter} onChange={setServiceFilter} options={serviceOptions} />
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Location</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Start Date</th>
                <FilterableTh label="Status" className="text-left px-4 py-3 text-xs"
                  value={activeTab} onChange={setActiveTab}
                  options={STATUS_TABS.map(tab => ({ key: tab, label: tab, count: counts[tab] }))} />
                <FilterableTh label="Coordinator" className="text-left px-4 py-3 text-xs"
                  value={coordinatorFilter} onChange={setCoordinatorFilter} options={coordinatorOptions} />
                <FilterableTh label="Entered Via" className="text-left px-4 py-3 text-xs"
                  value={enteredViaFilter} onChange={setEnteredViaFilter} options={enteredViaOptions} />
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9}className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="w-8 h-8 text-slate-200" />
                      <p className="text-sm text-slate-400">No service requests match your filters.</p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map(r => (
                <tr key={r.request_id} className="hover:bg-slate-50">

                  {/* Request */}
                  <td className="px-4 py-3 align-top">
                    <p className="font-semibold text-slate-900 leading-tight"><ClientLink id={r.client_id}>{withHonorific(r.client_honorific || r.honorific, r.payer_name)}</ClientLink></p>
                    <div className="flex items-center gap-1 mt-0.5 text-slate-500 text-xs">
                      <Phone className="w-3 h-3" />
                      <span>{formatMobileNumber(r.payer_mobile)}</span>
                    </div>
                    {r.service_request_code && (
                      <p className="text-xs text-slate-400 font-mono mt-0.5">{r.service_request_code}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(r.created_at)}</p>
                  </td>

                  {/* Care Profile */}
                  <td className="px-4 py-3 align-top">
                    <p className="font-medium text-slate-900"><PatientLink id={r.patient_id}>{r.patient_name || '—'}</PatientLink></p>
                    {(r.patient_age || r.relationship_to_client) && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {[r.patient_age && `Age ${r.patient_age}`, r.relationship_to_client].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {r.patient_condition && (
                      <p className="text-xs text-slate-400 mt-0.5 max-w-[160px] truncate">{r.patient_condition}</p>
                    )}
                  </td>

                  {/* Service */}
                  <td className="px-4 py-3 align-top">
                    {r.service_type === PRIORITY_TYPE ? (
                      <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100">
                        Priority Membership
                      </span>
                    ) : (
                      <p className="font-medium text-slate-900">{r.service_type || '—'}</p>
                    )}
                    {r.service_model && r.service_type !== PRIORITY_TYPE && (
                      <p className="text-xs text-slate-500 mt-0.5">{r.service_model.replace(/_/g, ' ')}</p>
                    )}
                    {r.preferred_gender && r.preferred_gender !== 'ANY' && (
                      <p className="text-xs text-slate-400 mt-0.5">Prefers {r.preferred_gender.toLowerCase()}</p>
                    )}
                  </td>

                  {/* Location */}
                  <td className="px-4 py-3 align-top max-w-[180px]">
                    <div className="flex items-start gap-1 text-slate-600">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-400" />
                      <span className="text-xs line-clamp-2">{r.location_address || '—'}</span>
                    </div>
                  </td>

                  {/* Start Date */}
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    <div className="flex items-center gap-1 text-slate-600 text-xs">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      <span>{formatDate(r.start_date)}</span>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-col gap-1">
                      <StatusBadge status={r.status || 'PENDING'} />
                      {r.status === 'BOOKING_CREATED' && r.booking_status && (
                        <BookingStatusBadge status={r.booking_status} />
                      )}
                    </div>
                  </td>

                  {/* Coordinator */}
                  <td className="px-4 py-3 align-top">
                    {r.coordinator_name ? (
                      <div className="flex items-center gap-1.5 text-xs text-slate-700">
                        <UserCog className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <span className="font-medium">{r.coordinator_name}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Unassigned</span>
                    )}
                  </td>

                  {/* Entered Via */}
                  <td className="px-4 py-3 align-top">
                    <EnteredViaBadge value={r.entered_via} />
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => navigate(`/admin/service-requests/${r.request_id}/summary`)}
                        title="View Summary"
                        className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-blue-600 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {(() => {
                        const action = getStageAction(r);
                        if (!action) return null;
                        const StageIcon = action.icon;
                        return (
                          <button
                            onClick={() => handleStageAction(r, action)}
                            title={action.title}
                            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-emerald-600 transition-colors"
                          >
                            <StageIcon className="w-4 h-4" />
                          </button>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-2.5 text-xs text-slate-400">
            Showing {filtered.length} of {requests.length} request{requests.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {paymentPicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPaymentPicker(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Record payment for which quotation?</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {paymentPicker.request.service_request_code || paymentPicker.request.payer_name}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPaymentPicker(null)}
                className="p-1 rounded text-slate-400 hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              {paymentPicker.loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              ) : paymentPicker.error ? (
                <div className="space-y-3">
                  <p className="text-sm text-red-600">Couldn't load this request's quotations: {paymentPicker.error}</p>
                  <button
                    type="button"
                    onClick={() => { const p = paymentPicker.fallbackPath; setPaymentPicker(null); navigate(p); }}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
                  >
                    Open the active quotation instead
                  </button>
                </div>
              ) : paymentPicker.quotes.map(q => (
                <QuoteOption
                  key={q.quote_id}
                  quote={q}
                  isActive={q.quote_id === paymentPicker.request.active_quote_id}
                  onSelect={sel => {
                    setPaymentPicker(null);
                    navigate(`/admin/quotations/${sel.quote_id}`);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {showNotificationSettings && canManageNotifications && (
        <NotificationRecipientsModal onClose={() => setShowNotificationSettings(false)} />
      )}

      <PresetManager
        isOpen={showPresetManager}
        onClose={() => setShowPresetManager(false)}
        onSave={() => setShowPresetManager(false)}
      />
    </AdminLayout>
  );
};

export default ServiceRequests;
